import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { useTheme } from '../../../../../theming/useTheme';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { getLineHeightBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { MoreButton } from './MoreButton';
import { SocialSectionDataProps } from '../../footer/AdditionalFooterSections/FooterSocialLinks';
import { SocialLinks } from './SocialLinks';
import { AdditionalStyleSheet } from '../../../../components.utils';
import { WebFontsContext } from '../../../../../theming/ThemeProviderWithFonts';
import { HUGE_SCREEN_SIZE, MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { Logo, LogoDataProps } from './Logo';
import { InjectedVideoLogoApiProps, VideoLogo } from './VideoLogo';
import { LinkButtonDataProps } from '../../utils';
import { FixedNav } from './FixedNav';
import { Menu } from './menu/Menu';
import { componentConfigThemeKey, HeaderWithSubMenusThemeProps } from './HeaderWithSubMenus.theme';
import { Editions } from './Editions';
import { SubMenu } from './SubMenu';
import { HeaderAnimation, HeaderAnimationContext } from '../HeaderAnimation';
import { useHeaderHeights } from './useHeaderHeights';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { reducedHeaderFirstRowHeightFactors } from '../header.utils';
import { ScrollableNav } from './ScrollableNav';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';
import { MenuLinkDataProps } from '../primaryHeader/menu/links/MenuSubLink';

interface MenuData {
  links: MenuLinkDataProps[];
  buttons?: Array<LinkButtonDataProps>;
}

export interface HeaderWithSubMenusDataProps {
  links: NavigationLink[] | null;
  logo: LogoDataProps;
  editions: NavigationLink[] | null;
  menu: MenuData | null;
  videoLogo: InjectedVideoLogoApiProps | null;
  pagePath: string;
  editionEndpoint: string;
  socialSection: SocialSectionDataProps | null;
  moreButtonText: string;
}

const getStyles = (themeProps: HeaderWithSubMenusThemeProps, menuFoldState: AnimationDirectionType, animationDuration: number) => {
  const {
    firstRowBackgroundColor,
    baseUnit,
    firstRowHeightInLargeScreen,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    fontSizesStyles,
    linkFontSizeLarge,
    boxShadow,
  } = themeProps;
  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0,
      boxShadow,
    },
    headerFirstRow: {
      backgroundColor: firstRowBackgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 100,
      position: 'relative',
      transition: `height ${animationDuration}ms ease 0s`,
      [MEDIA_BREAKPOINTS.large]: {
        height: menuFoldState === AnimationDirection.UP ? `${firstRowHeightInLargeScreen * reducedHeaderFirstRowHeightFactors.large * baseUnit}px` : `${firstRowHeightInLargeScreen * baseUnit}px`,
        padding: `0 max(${baseUnit * verticalGapInLargeScreen}px, calc((100% - ${HUGE_SCREEN_SIZE}px) / 2))`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${firstRowHeightInMediumScreen * baseUnit}px`,
        padding: `0 ${baseUnit * verticalGapInMediumScreen}px`,
        justifyContent: 'space-between',
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${firstRowHeightInSmallScreen * baseUnit}px`,
        padding: `0 ${baseUnit * verticalGapInSmallScreen}px`,
        justifyContent: 'space-between',
      },
    },
    headerFirstRowItemsContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    fixedNav: {
      [MEDIA_BREAKPOINTS.large]: {
        height: `${getLineHeightBySize(fontSizesStyles, linkFontSizeLarge, 'large')}px`,
        overflow: 'hidden',
        display: 'block',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
    moreMenuLinksWrapper: {
      position: 'absolute',
      left: 0,
      top: `${firstRowHeightInLargeScreen * baseUnit - baseUnit * 2}px`,
    },
  });
};

const showMoreButtonStyle = (buttonOffset: number, shouldShowMoreButton: boolean): AdditionalStyleSheet => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: shouldShowMoreButton ? 'flex' : 'none',
        transform: `translateX(-${buttonOffset}px)`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
  }) as AdditionalStyleSheet;
};

const createMenuStyle = (baseUnit: number, verticalGapInMediumScreen: number, verticalGapInSmallScreen: number): AdditionalStyleSheet => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex',
        marginRight: `${baseUnit * verticalGapInMediumScreen}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex',
        marginRight: `${baseUnit * verticalGapInSmallScreen}px`,
      },
    },
  }) as AdditionalStyleSheet;
};

const createHeaderStyleWithAnimation = (animationDuration: number) => {
  return StyleSheet.create({
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)',
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)',
      },
    },
  });
};

const getInvisibleLinks = (parent: HTMLUListElement | null) => {
  if (!parent) return [];
  const baseline = parent.getBoundingClientRect().top;
  return Array.from(parent.childNodes).filter(child => {
    return (child as HTMLElement).getBoundingClientRect().top > baseline;
  });
};

const getButtonOffset = (listElement: HTMLUListElement, moreButtonOriginalPosition: number, baseUnit: BaseUnit, verticalGap: number) => {
  return moreButtonOriginalPosition - listElement.getBoundingClientRect().right - (baseUnit * verticalGap);
};

const getMoreMenuLinks = (invisibleLinks: ChildNode[], links: NavigationLink[]) => {
  const numLinks = invisibleLinks.length;
  return links.slice(links.length - numLinks);
};

const getHeaderStyleWithAnimation = (menuFoldState: AnimationDirectionType, animationDuration: number) => {
  const style = createHeaderStyleWithAnimation(animationDuration);
  return menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
};

export const HeaderWithSubMenusComponent: React.FunctionComponent<HeaderWithSubMenusDataProps> = props => {
  const { videoLogo, logo, links, moreButtonText, socialSection, pagePath, menu, editions, editionEndpoint } = props;
  const { menuFoldState, animationDuration } = React.useContext<HeaderAnimationContext>(HeaderAnimationContext);
  const themeProps = useTheme<HeaderWithSubMenusThemeProps>(componentConfigThemeKey);
  const {
    linkColorTypeHover,
    linkColorTypeActive,
    linkColorTypeNormal,
    baseUnit,
    fontSizesStyles,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    logoHeightInLargeScreen,
    logoHeightInMediumScreen,
    logoHeightInSmallScreen,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    linkDropdownIconColor,
    firstRowHeightInLargeScreen,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    subMenuLinksBackgroundColor,
    subMenuLinksSecondLevelBackgroundColor,
    secondRowBackgroundColor,
    secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen,
    editionsSeparatorColor,
    editionsMenuBackgroundColor,
  } = themeProps;
  const ulRef = React.useRef<HTMLUListElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [moreButtonOffset, setMoreButtonOffset] = React.useState(0);
  const [moreButtonOriginalPosition, setMoreButtonOriginalPosition] = React.useState(-1);
  const [shouldShowMoreLinksMenu, setShouldShowMoreLinksMenu] = React.useState(false);
  const navHeightLarge = getLineHeightBySize(fontSizesStyles, linkFontSizeLarge, 'large');
  const styles = getStyles(themeProps, menuFoldState, animationDuration);
  const shouldShowSocial = socialSection && socialSection.imageLinks.length > 0;
  const shouldEditions = editions && editions.length > 0;
  const webFontsContext = React.useContext(WebFontsContext);

  const invisibleLinks = getInvisibleLinks(ulRef.current);
  const shouldShowMoreButton = invisibleLinks.length > 0;
  const moreButtonStyle = showMoreButtonStyle(moreButtonOffset, shouldShowMoreButton);
  const menuStyle = createMenuStyle(baseUnit, verticalGapInMediumScreen, verticalGapInSmallScreen);

  React.useEffect(() => {
    if (buttonRef.current && moreButtonOriginalPosition === -1 && shouldShowMoreButton) {
      const { left } = buttonRef.current.getBoundingClientRect();
      if (left > 0) {
        setMoreButtonOriginalPosition(left);
      }
    }
  }, [buttonRef, moreButtonOriginalPosition, setMoreButtonOriginalPosition, shouldShowMoreButton]);

  React.useEffect(() => {
    if (ulRef.current !== null && buttonRef.current !== null) {
      if (invisibleLinks.length > 0 && menuFoldState === AnimationDirection.NONE) {
        const offset = getButtonOffset(ulRef.current, moreButtonOriginalPosition, baseUnit, verticalGapInLargeScreen);
        setMoreButtonOffset(offset);
      }
    }
  }, [ulRef, buttonRef, webFontsContext, baseUnit, moreButtonOriginalPosition, verticalGapInLargeScreen, invisibleLinks.length, menuFoldState]);

  const onToggleMoreLinksMenu = () => {
    setShouldShowMoreLinksMenu(!shouldShowMoreLinksMenu);
  };

  return (
    <ErrorBoundary>
      <header className={css(getHeaderStyleWithAnimation(menuFoldState, animationDuration), styles.header)}>
        <div className={css(styles.headerFirstRow)}>
          <div className={css(styles.headerFirstRowItemsContainer)}>
            {menu ? <Menu links={menu.links} editions={editions} editionEndpoint={editionEndpoint} buttons={menu.buttons} additionalStyle={menuStyle} /> : null}
            {videoLogo
              ? (
                <VideoLogo
                  {...videoLogo}
                  href={logo.href}
                  firstRowHeightInSmallScreen={firstRowHeightInSmallScreen}
                  firstRowHeightInMediumScreen={firstRowHeightInMediumScreen}
                  firstRowHeightInLargeScreen={firstRowHeightInLargeScreen}
                  menuAnimation={menuFoldState}
                  reduceHeightAnimationDuration={animationDuration}
                />
              )
              : (
                <Logo
                  {...logo}
                  logoHeightInLargeScreen={logoHeightInLargeScreen}
                  logoHeightInMediumScreen={logoHeightInMediumScreen}
                  logoHeightInSmallScreen={logoHeightInSmallScreen}
                  menuAnimation={menuFoldState}
                  reduceHeightAnimationDuration={animationDuration}
                />
              )}
            <FixedNav
              rowHeight={firstRowHeightInLargeScreen}
              navHeightLarge={navHeightLarge}
              ulRef={ulRef}
              links={links}
              pagePath={pagePath}
              activeStateColor={linkColorTypeActive}
              hoverStateColor={linkColorTypeHover}
              idleColor={linkColorTypeNormal}
              linkFontSizeLarge={linkFontSizeLarge}
              linkFontSizeMedium={linkFontSizeMedium}
              linkFontSizeSmall={linkFontSizeSmall}
              linkDropdownIconColor={linkDropdownIconColor}
              menuFoldState={menuFoldState}
              animationDuration={animationDuration}
            />
            <MoreButton
              refObject={buttonRef}
              moreButtonText={moreButtonText}
              additionalStyle={moreButtonStyle}
              fontSizeSmall={linkFontSizeSmall}
              fontSizeMedium={linkFontSizeMedium}
              fontSizeLarge={linkFontSizeLarge}
              colorTypeHover={linkColorTypeHover}
              colorTypeNormal={linkColorTypeNormal}
              colorTypeActive={linkColorTypeActive}
              dropdownIconColor={linkDropdownIconColor}
              onToggleMenu={onToggleMoreLinksMenu}
            />
            {
              shouldShowMoreLinksMenu && (
                <div className={css(styles.moreMenuLinksWrapper)}>
                  <SubMenu
                    menuFoldState={menuFoldState}
                    animationDuration={animationDuration}
                    links={getMoreMenuLinks(invisibleLinks, links!)}
                    parentElement={buttonRef.current!}
                    activeStateColor={linkColorTypeActive}
                    hoverStateColor={linkColorTypeHover}
                    idleColor={linkColorTypeNormal}
                    pagePath={pagePath}
                    linkFontSizeLarge={linkFontSizeLarge}
                    linkFontSizeMedium={linkFontSizeMedium}
                    linkFontSizeSmall={linkFontSizeSmall}
                    backgroundColor={subMenuLinksBackgroundColor}
                    subMenuLinksSecondLevelBackgroundColor={subMenuLinksSecondLevelBackgroundColor}
                    shouldOffsetLeft={false}
                  />
                </div>
              )
            }
          </div>
          <div className={css(styles.headerFirstRowItemsContainer)}>
            {shouldShowSocial && (
            <SocialLinks
              socialLinks={socialSection!.imageLinks}
              verticalGapInLargeScreen={verticalGapInLargeScreen}
              verticalGapInMediumScreen={verticalGapInMediumScreen}
            />
            )}
            {shouldEditions && (
            <Editions
              editions={editions!}
              editionEndpoint={editionEndpoint}
              verticalGapInLargeScreen={verticalGapInLargeScreen}
              verticalGapInMediumScreen={verticalGapInMediumScreen}
              verticalGapInSmallScreen={verticalGapInSmallScreen}
              fontSizeSmall={linkFontSizeSmall}
              fontSizeMedium={linkFontSizeMedium}
              fontSizeLarge={linkFontSizeLarge}
              colorTypeHover={linkColorTypeHover}
              colorTypeNormal={linkColorTypeNormal}
              colorTypeActive={linkColorTypeActive}
              dropdownIconColor={linkDropdownIconColor}
              editionsSeparatorColor={editionsSeparatorColor}
              editionsMenuBackgroundColor={editionsMenuBackgroundColor}
            />
            )}
          </div>
        </div>
        <ScrollableNav
          menuFoldState={menuFoldState}
          animationDuration={animationDuration}
          links={links}
          linkColorTypeActive={linkColorTypeActive}
          linkColorTypeHover={linkColorTypeHover}
          linkColorTypeNormal={linkColorTypeNormal}
          pagePath={pagePath}
          baseUnit={baseUnit}
          fontSizesStyles={fontSizesStyles}
          linkFontSizeLarge={linkFontSizeLarge}
          linkFontSizeMedium={linkFontSizeMedium}
          linkFontSizeSmall={linkFontSizeSmall}
          verticalGapInLargeScreen={verticalGapInLargeScreen}
          verticalGapInMediumScreen={verticalGapInMediumScreen}
          verticalGapInSmallScreen={verticalGapInSmallScreen}
          secondRowBackgroundColor={secondRowBackgroundColor}
          secondRowHeightInMediumScreen={secondRowHeightInMediumScreen}
          secondRowHeightInSmallScreen={secondRowHeightInSmallScreen}
        />
      </header>
    </ErrorBoundary>
  );
};

export const HeaderWithSubMenus: React.FunctionComponent<HeaderWithSubMenusDataProps> = props => {
  const headerHeights = useHeaderHeights();
  return (
    <ErrorBoundary>
      <HeaderAnimation headerHeights={headerHeights}>
        <HeaderWithSubMenusComponent {...props} />
      </HeaderAnimation>
    </ErrorBoundary>
  );
};
